import {
  Box,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/browser';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  utils as commonUtils,
  error as commonError,
} from '@finley-technologies/common';
import { Explo } from '@finley-technologies/finley-types';

import { useGenerateExploApiJwtQuery } from '../requests/explo.gql.generated';
import { generateMissingIntegrationMessage } from '../utils';

type ExploDashboardContainerProps = {
  organizationId: string;
  creditFacilityId?: string;
  exploPlacement: Explo.ExploPlacement.AllOptionsType;
};
export const ExploDashboardContainer = ({
  creditFacilityId,
  organizationId,
  exploPlacement,
}: ExploDashboardContainerProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const { data: tokenData } = useGenerateExploApiJwtQuery({
    fetchPolicy: 'network-only',
    variables: {
      organizationId,
      creditFacilityId,
      exploArtifactType: 'dashboard',
      exploPlacement,
    },
    onError: (error) => {
      Sentry.captureException(
        new commonError.FinleyError(
          `Failed to generate Explo API JWT for Dashboard [error:${error.message}]`,
          {
            metadata: {
              logMeta: {
                error,
                organizationId,
                creditFacilityId,
                exploPlacement,
              },
            },
          },
        ),
      );

      toast({
        title:
          'An error occurred while attempting to view strats dashboard. Please contact support@finleycms.com if this continues.',
        status: 'error',
        position: 'top',
        duration: 6000,
        isClosable: true,
      });
      navigate('/');
    },
  });

  if (!tokenData) {
    return <Skeleton />;
  }

  if (!tokenData.tokenResponse.isIntegrationAvailable) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertDescription>
          {generateMissingIntegrationMessage({
            exploArtifactType: 'dashboard',
            exploPlacement,
          })}
        </AlertDescription>
      </Alert>
    );
  }

  if (tokenData.tokenResponse.error) {
    Sentry.captureException(
      new commonError.FinleyError(
        `Failed to fetch Explo JWT [type:dashboard] [placement:${exploPlacement}] [error:${tokenData.tokenResponse.error}]`,
        { metadata: { logMeta: { organizationId, creditFacilityId } } },
      ),
    );
    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertDescription>
          An error occurred when fetching Strats Dashboard. Please contact
          Finley support for assistance if this continues.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box id="explo-dashboard-container">
      <explo-dashboard
        dash-jwt={tokenData.tokenResponse.jwt}
        refresh-minutes={5}
        environment={commonUtils.EnvUtils.getEnv()}
        updateUrlParams
      />
    </Box>
  );
};
